import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import 'devbridge-autocomplete/dist/jquery.autocomplete';

import postal from 'postal';
import Loading from '../../../../shared/loading';
import ErrorBoundary from '../../../../shared/error-boundary';
import defaultOnErrorHandler from '../../../../shared/default-on-error-handler';
import StockCodeTooltip from '../../stock-codes-components/tooltip';
import breakpoints from '../../../../shared/breakpoints';
import { listAllStockCodes } from '../../../../services/stock-codes/stock-codes-service';
import { getWireStockCodes } from '../../../../services/stock-codes/from-wires-service';
import { add, remove } from '../../../../services/wire/edit-stock-codes-service';

const isMobile = breakpoints.upTo_medium();

export default class StockCodesControl extends React.Component {
  static propTypes = {
    wireId: PropTypes.number.isRequired,
    stockCodes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        stockExchange: PropTypes.string,
        code: PropTypes.string,
        companyName: PropTypes.string,
        isStockCodeFollowedByCurrentUser: PropTypes.bool,
        mentioned: PropTypes.bool,
      }),
    ).isRequired,
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      stockCodes: this.props.stockCodes,
    };
    this.stockCodes = [];
    this.addStockCode = this.addStockCode.bind(this);
    this.getStockCodes = this.getStockCodes.bind(this);
    this.filterItem = this.filterItem.bind(this);
    this.buildItems = this.buildItems.bind(this);

    postal.subscribe({
      channel: 'editor',
      topic: 'wire-body-saved',
      callback: this.getStockCodes,
    });
  }

  componentDidMount() {
    this.loading.start();
    listAllStockCodes()
      .then(result => {
        this.stockCodes = result.data.stockCodes.map(s => ({ ...s, category: 'Select a stock code' }));
        // Refer to https://github.com/devbridge/jQuery-Autocomplete for API doc
        // instead of the official jQuery.AutoComplete. They're different!
        this.$newInput.devbridgeAutocomplete({
          autoSelectFirst: true,
          lookup: this.stockCodes.map(d => ({ value: d.code, data: d })),
          lookupFilter: this.filterItem,
          lookupLimit: 10,
          minChars: 2,
          noSuggestionNotice: 'No Stock Codes Found',
          onSelect: this.addStockCode,
          tabDisabled: true,
          triggerSelectOnValidInput: false,
          showNoSuggestionNotice: true,
          maxHeight: 350,
          groupBy: 'category',
          beforeRender: this.buildItems,
        });
      })
      .catch(e => defaultOnErrorHandler(e, 'Error getting stock codes'))
      .finally(() => this.loading.stop());
  }

  getStockCodes() {
    getWireStockCodes(this.props.wireId)
      .then(result => this.setState({ stockCodes: result.data.stockCodes }));
  }

  buildItems(container, suggestions) {
    const pattern = `(${this.queryLowerCase.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')})`;

    container.find('.autocomplete-suggestion').each((index, suggestionItem) => {
      const matchedCompanyName = suggestions[index].data.companyName
        .replace(new RegExp(pattern, 'gi'), '<strong>$1</strong>')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/&lt;(\/?strong)&gt;/g, '<$1>');

      const newHtml = `
        <div class="line-clamp-1 break-all">${matchedCompanyName}</div>
        <div>${suggestionItem.innerHTML}</div>
      `;

      suggestionItem.innerHTML = newHtml;
      suggestionItem.classList.add('grid', 'gap-1');
    });
  }

  filterItem(suggestion, originalQuery, queryLowerCase) {
    this.queryLowerCase = queryLowerCase;
    const isMatched = suggestion.value.toLowerCase().indexOf(queryLowerCase) !== -1;
    return isMatched || suggestion.data.companyName.toLowerCase().indexOf(queryLowerCase) !== -1;
  }

  addStockCode(evt) {
    const { code, slug } = evt.data;
    // skip invalid code
    if (!_.find(this.stockCodes, { code })) {
      this.$newInput.val('').focus();
      return;
    }

    // skip existing code
    if (_.find(this.state.stockCodes, sc => sc.code === code)) {
      this.$newInput.val('').focus();
      return;
    }

    this.loading.start();
    add(this.props.wireId, slug)
      .then(result => this.setState(state => update(state, {
        stockCodes: { $push: [result.data] },
      })))
      .catch(e => defaultOnErrorHandler(e, 'Error adding stock code'))
      .finally(() => this.loading.stop());

    this.$newInput.val('').focus();
  }

  removeStockCode(evt, d) {
    evt && evt.preventDefault && evt.preventDefault();
    // Remove phantom tooltip elements
    $('.tooltip[role="tooltip"]').remove();

    this.loading.start();
    remove(this.props.wireId, d.slug)
      .then(() => this.setState(state => update(state, {
        stockCodes: stockCodes => stockCodes.filter(e => e.slug !== d.slug),
      })))
      .catch(e => defaultOnErrorHandler(e, 'Error removing stock code'))
      .finally(() => this.loading.stop());
  }

  render() {
    return (
      <ErrorBoundary>
        <aside className="sidebar-item">
          <Loading variant="inline" ref={el => (this.loading = el)}>
            <section className="form-group">
              <input
                type="text"
                className="sidebar-item-input-autocomplete"
                ref={el => (this.$newInput = $(el))}
                placeholder="type stock code"
              />
              {!!this.state.stockCodes.length && (
                <ul className="sidebar-item-ulist stock-codes-container">
                  {this.state.stockCodes.map(d => {
                    const id = `stock-codes-${d.slug}`;
                    const text = `${d.stockExchange}:${d.code}`;
                    return (
                      <li
                        className="sidebar-item-list-item stock-code-item"
                        key={d.slug}
                        data-tip
                        data-for={id}
                      >
                        {text}
                        {!d.mentioned && <span className="visible-on-hover" role="cell" onClick={e => this.removeStockCode(e, d)}>&times;</span>}
                        {!isMobile && (
                          <StockCodeTooltip tooltipId={id} stockCode={d} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </Loading>
        </aside>
      </ErrorBoundary>
    );
  }
}
